/**
 * @fileoverview MarketingParameters Component
 *
 * @typedef {import('../types/ConsentChangeEvent').ConsentChangeEventDetail} ConsentChangeEventDetail
 * @typedef {import('../types/ConsentChangeEvent').ConsentChangeEvent} ConsentChangeEvent
 * @typedef {import('../types/ConsentChangeEvent').CategoryConsentMap} CategoryConsentMap
 * @typedef {import('../types/ConsentChangeEvent').ConsentValue} ConsentValue
 *
 * This component manages the integration between Optimus Marketing API and Hurra tracking.
 * It orchestrates consent-aware parameter fetching and tracking event delivery with asynchronous loading.
 *
 * LOGIC FLOW:
 *
 * 1. INITIALIZATION - FALLBACK PARAMETERS:
 *    - On mount, immediately sets fallback parameters from window.tracking
 *    - These fallback parameters serve as baseline until Optimus provides updated values
 *    - Fallback includes hurraInstanceId needed for Hurra script loading
 *
 * 2. PARALLEL ASYNC OPERATIONS (INDEPENDENT):
 *    a) HURRA SCRIPT LOADING:
 *       - Loads Hurra tracking script with fallback parameters
 *       - For iOS apps: Checks ATT permission first, resets consent if denied
 *       - Includes retry logic (max 3 attempts with 1s delay)
 *       - Sets isHurraLoaded flag when script is ready
 *
 *    b) OPTIMUS API CALL (CONSENT-GATED):
 *       - Starts immediately if cookie consent is present
 *       - If no cookie consent: checks SDK after Hurra loads, then starts Optimus if consent
 *       - OptimusService handles retry (max 3 attempts)
 *       - On success: Updates parameters
 *       - On max attempts: Keeps fallback parameters
 *
 * 3. TRACKING EVENT PUSH (ONCE):
 *    - Waits for Hurra script to load
 *    - If Optimus was started: waits for it to finish
 *    - Otherwise checks SDK consent and optionally calls Optimus
 *    - Pushes exactly one event with final parameters
 *
 * 4. CONSENT CHANGE HANDLING:
 *    - Listens for 'consent::change' DOM event
 *    - If consent granted and Optimus not started yet, starts Optimus
 *
 * KEY IMPROVEMENTS:
 * - Hurra loading and Optimus API call are truly independent (parallel async operations)
 * - Optimus call starts early based on consent cookies (doesn't wait for Hurra)
 * - Tracking events only push when both Hurra is loaded AND final parameters are ready
 * - Consent check uses cookies when Hurra SDK is not yet available
 *
 * SERVICES USED:
 * - OptimusService: API calls, retry logic, polling management
 * - ConsentService: Consent cookie checking, GDPR ATT cookie management
 * - IOsAttService: iOS ATT permission handling
 * - HurraEventService: Tracking event creation and delivery
 * - OwaParamsHelper: OWA parameter object creation and management
 *
 * @module components/MarketingParameters
 * @requires react
 * @requires OptimusService
 * @requires ConsentService
 * @requires IOsAttService
 * @requires HurraEventService
 * @requires OwaParamsHelper
 */

// noinspection ES6UnusedImports
import {useEffect, useState, useRef, useCallback} from 'react';
import Logger from '@core/scripts/components/logger';
import Messages from '@core/scripts/components/messages.js';
import MessageBroker from 'deprecated-shared-ui/components/message-broker.js';
import OptimusService from '../services/OptimusService';
import ConsentService from '../services/ConsentService';
import IOsAttService from '../services/IOsAttService';
import HurraEventService from '../services/HurraEventService';
import HurraScriptService from '../services/HurraScriptService';
import OwaParamsHelper from '../helpers/OwaParamsHelper';
import {ConsentValue} from '../types/ConsentChangeEvent';

// Nitro first-party cookie, set by the Nitro vendor script once the user opts in.
// Must be cleared whenever that specific vendor is not explicitly accepted.
const NITRO_COOKIE_NAME = 'FP_NITRO';
const NITRO_VENDOR_CATEGORY = 'ADVERTISEMENT';
const NITRO_VENDOR_ID = 'm1510';

/**
 * Clears the Nitro first-party cookie unless the Nitro vendor has been
 * explicitly accepted (ConsentValue.ACCEPTED).
 *
 * Any other state - MIXED, DECLINED, or the vendor being absent entirely
 * (`undefined`, e.g. before consent data has loaded, or the vendor list
 * simply doesn't include it) - is treated as "not consented" and results
 * in the cookie being removed. This fail-safe default avoids leaking the
 * cookie when consent status is unknown.
 *
 * @param {ConsentChangeEvent} event - The consent::change DOM event
 */
function removeNitroCookieIfNotConsented(event) {
    const nitroConsent = event?.detail?.vendors?.[NITRO_VENDOR_CATEGORY]?.[NITRO_VENDOR_ID];

    if (nitroConsent !== ConsentValue.ACCEPTED) {
        document.cookie = `${NITRO_COOKIE_NAME}=; Max-Age=0; path=/`;
    }
}

export default function MarketingParameters({pageType, hurraScriptUrl, traceId}) {
    // State: Hurra tracking instance identifier
    const [hurraInstanceId, setHurraInstanceId] = useState('');

    // State: Tracks if Hurra script is loaded/ready
    // Note: This can be true even if script failed (after max retries, we mark as loaded to prevent loops)
    // Check window.hc_tm existence to verify actual availability
    const [isHurraLoaded, setIsHurraLoaded] = useState(false);

    // Ref: Tracks if tracking event has been pushed (ensures single push)
    const eventPushedRef = useRef(false);

    // Service instance for API calls and retry management
    const optimusServiceRef = useRef(new OptimusService());

    // Ref: Tracks if Hurra script load has started (prevents duplicate starts)
    const hurraLoadStartedRef = useRef(false);

    /**
     * Sets fallback parameters from window.tracking.
     *
     * This is always called on initialization to establish baseline parameters.
     * These parameters are used initially until/unless Optimus API provides updated values.
     *
     * Used in two scenarios:
     * 1. On mount: Initial baseline parameters for immediate Hurra script loading
     * 2. On API max retries: Keeps existing fallback when Optimus is unavailable
     */
    const setFallbackParameters = useCallback(() => {
        OwaParamsHelper.updateWindowOwaParams(null, traceId);
        const instanceId = window.tracking?.hurraInstanceId ?? '';
        setHurraInstanceId(instanceId);
        return instanceId;
    }, [traceId]);

    /**
     * Starts Hurra script loading as soon as we have an instance ID.
     *
     * @param {string} instanceId - Hurra instance ID
     */
    const startHurraLoad = useCallback((instanceId) => {
        if (!instanceId || hurraLoadStartedRef.current) return;
        hurraLoadStartedRef.current = true;
        const attOptInStatus = IOsAttService.checkAttPermissionAndResetIfNeeded();
        HurraScriptService.loadWithRetry({
            hurraInstanceId: instanceId,
            hurraScriptUrl,
            attOptInStatus,
            maxAttempts: 3,
            retryDelayMs: 1000
        }).then((loaded) => {
            setIsHurraLoaded(true);
            if (!loaded) {
                Logger.warn('Hurra script unavailable after retries. Continuing with fallback parameters only.');
            }
        });
    }, [hurraScriptUrl]);

    /**
     * Pushes a tracking event to Hurra manually (when Hurra script is already loaded).
     *
     * Uses window.owaParams which must be set before calling.
     */
    const manualEventPush = useCallback(() => {
        try {
            if (typeof window.hc_tm === 'undefined') {
                Logger.warn('Hurra tracking system not available. Event push skipped.');
                return;
            }
            HurraEventService.pushTrackingEvent(window.owaParams);
        } catch (error) {
            Logger.error('Failed to push tracking event to Hurra', error);
        }
    }, []);

    /**
     * Starts Optimus call once and stores the in-flight promise.
     *
     * @returns {Promise<{parameters: Object, hurraInstanceId: string}|null>}
     */
    const requestOptimus = useCallback(async () => {
        const data = await optimusServiceRef.current.getLatestResultOrPromise({pageType, traceId});

        if (data) {
            OwaParamsHelper.updateWindowOwaParams(data.parameters, traceId);
            setHurraInstanceId(data.hurraInstanceId);
        }

        return data ?? null;
    }, [pageType, traceId]);

    /**
     * Handles consent change DOM events.
     * Waits for Optimus (existing or new request) and pushes once again.
     *
     * @param {ConsentChangeEvent} event - The consent::change DOM event
     */
    const handleConsentChange = useCallback(async (event) => {
        const categories = event.detail?.categories;
        if (!categories) return;

        ConsentService.updateConsentCookiesFromCategories(categories);
        removeNitroCookieIfNotConsented(event);

        if (ConsentService.hasAny3rdPartyConsent(categories)) {
            await requestOptimus();
            manualEventPush();
        }
    }, [manualEventPush, requestOptimus, traceId]);

    /**
     * EFFECT: Initialize fallback parameters and start Hurra load immediately.
     */
    useEffect(() => {
        const instanceId = setFallbackParameters();
        startHurraLoad(instanceId);
    }, [setFallbackParameters, startHurraLoad]);

    /**
     * EFFECT: When Hurra is loaded, wait for Optimus if pending or consented,
     * then push the initial event once.
     */
    useEffect(() => {
        if (!isHurraLoaded || eventPushedRef.current) return;

        const run = async () => {
            if (optimusServiceRef.current.hasPendingRequest() || ConsentService.has3rdPartyConsent()) {
                await requestOptimus();
            }

            if (!eventPushedRef.current) {
                manualEventPush();
                eventPushedRef.current = true;
            }
        };

        run();
    }, [isHurraLoaded, traceId, manualEventPush, requestOptimus]);

    /**
     * EFFECT: Subscribe to consent change DOM event.
     *
     * On consent opt-in, wait for Optimus and push one additional event.
     */
    useEffect(() => {
        document.addEventListener('consent::change', handleConsentChange);

        return () => {
            document.removeEventListener('consent::change', handleConsentChange);
        };
    }, [handleConsentChange]);

    /**
     * EFFECT: Subscribe to cart and wishlist events via HurraEventService
     *
     * Delegates MessageBroker subscription handling to HurraEventService,
     * which handles cart and wishlist events internally.
     */
    useEffect(() => {
        const unsubscribe = HurraEventService.subscribeToEvents(MessageBroker, Messages);

        return () => {
            unsubscribe();
        };
    }, []);


    /**
     * EFFECT: Cleanup - stop polling on unmount
     */
    useEffect(() => {
        return () => {
            optimusServiceRef.current.stopPolling();
        };
    }, []);


    return null; // This component has no visual output
}